import type { VariantProps } from 'class-variance-authority';
import type { StaticImageData } from 'next/image';
import type { ProviderDto } from '@/queries/providers/providers.schema';
import { cva } from 'class-variance-authority';
import Image from 'next/image';
import { RatingStars } from '@/components/shared/RatingStars';
import { AppImage } from '@/components/ui/AppImage/AppImage';
import { AppRoutes } from '@/i18n/constants';
import { Link } from '@/i18n/routing';
import { cn } from '@/utils/cn';
import { ProviderCardGlow } from './ProviderCardGlow';

const providerCardVariants = cva(
  'relative w-full rounded-[0.75rem] overflow-hidden',
  {
    variants: {
      variant: {
        default: 'md:max-w-51.5 md:px-6 h-24 sm:h-34.25 px-2.5',
        lobby: 'h-12.5 md:h-[5rem] md:px-0 rounded-[0.5rem] md:rounded-[0.75rem]',
      },

    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

interface ProviderCardProps extends VariantProps<typeof providerCardVariants> {
  provider: {
    alias: string;
    name: string;
    cover?: {
      path: string | StaticImageData;
      key?: string;
    };
    mobileListCover?: {
      path: string | StaticImageData;
      key?: string;
    };
  } | ProviderDto;
  hideName?: boolean;
  cardClassName?: string;
  className?: string;
  priority?: boolean;
  hasRating?: boolean;
}

export default function ProviderCard({ provider, className, cardClassName, variant = 'default', hideName = false, priority = false, hasRating = false }: ProviderCardProps) {
  return (
    <Link href={AppRoutes.providersSlugPaged(provider.alias)} className={cn('group', className)}>
      <div className={cn(providerCardVariants({ variant }), '', cardClassName)}>

        <ProviderCardGlow
          className="pointer-events-none absolute inset-0 z-0"
          variant="lobby"
        />
        {variant === 'lobby' && provider.mobileListCover && (
          <div className="relative w-full h-full flex items-center justify-center md:hidden">
            <Image
              fill
              src={provider.mobileListCover.key ?? provider.mobileListCover.path}
              alt={provider.name}
              sizes="(max-width: 767px) 7.5rem, 0px"
              priority={priority}
              className="w-full h-full object-contain absolute inset-0"
            />
          </div>
        )}

        {provider?.cover && (
          <div className="relative w-full h-full flex items-center justify-center">
            <AppImage
              src={provider.cover.key ?? provider.cover.path}
              alt={provider.name}
              variant="provider-cover"
              priority={priority}
              className="w-full h-full object-contain absolute inset-0"
            />
          </div>
        )}
      </div>
      {!hideName && <p className="text-light paragraph-s-semibold mt-1.75">{provider.name}</p>}
      {/* TODO: add rating when BE will provide it */}
      {hasRating && <RatingStars rating={5} showValue={true} size="sm" interactive className="mt-0.5" />}
    </Link>
  );
};
